import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import Layout from "../components/Layout";
import { useApiStore } from "../store";
import usePageMetadata from "../hooks/usePageMetadata";
import useI18n from "../hooks/useI18n";

const SignInScreen = () => {
  const navigate = useNavigate();
  const { defaultPage, localDB } = usePageMetadata();
  const { t } = useI18n();

  const { clearLocalStorage, signin } = useApiStore((state) => state);

  useEffect(
    () => {
      clearLocalStorage();
    },
    // eslint-disable-next-line
    [],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const username = data.get("username")?.toString() || "";
    const password = data.get("password")?.toString() || "";

    const { authenticated, requirePasswordChange, session } = await signin(
      username,
      password,
    );

    if (requirePasswordChange) {
      navigate("/changepassword", {
        state: { username, session, forceChange: true },
      });
      return;
    }

    // Re-read authorizedPages from localStorage since signin just updated it
    const authorized_pages_key = `${localDB.name}_authorized_pages`;
    const freshAuthorizedPagesString =
      localStorage.getItem(authorized_pages_key);
    const freshAuthorizedPages = freshAuthorizedPagesString
      ? JSON.parse(freshAuthorizedPagesString)
      : [];

    const authorizedDefaultPage =
      freshAuthorizedPages.includes(defaultPage) ||
      freshAuthorizedPages.length === 0
        ? defaultPage
        : freshAuthorizedPages[0];

    // All authorized users should see the list view (data grid)
    const navigationDestination = `/${authorizedDefaultPage}`;

    if (authenticated) navigate(navigationDestination);
  };

  return (
    <Layout>
      <Container component="main" sx={{ maxWidth: { xs: "sm", md: "sm" } }}>
        <Paper
          sx={{
            marginTop: 8,
            padding: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOpenIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {t("auth.sign_in")}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={t("auth.username")}
              name="username"
              autoComplete="username"
              autoFocus
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t("auth.password")}
              type="password"
              id="password"
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              size="large"
            >
              {t("auth.sign_in")}
            </Button>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Link
                href="#"
                onClick={() => navigate("/forgotpassword")}
                variant="body2"
              >
                {t("auth.forgot_password")}
              </Link>
              <Link
                href="#"
                onClick={() => navigate("/register")}
                variant="body2"
              >
                {t("auth.dont_have_account")}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Layout>
  );
};

export default SignInScreen;
